import { useRankingsNetworkSummaryQuery } from '../api/queries';
import type { RankingsLaggard, RankingsTopPerformer } from '../types';

type Props = {
  onDrillIn: (afNumber: string) => void;
};

export default function RankingsSection({ onDrillIn }: Props) {
  const { data, isLoading, error } = useRankingsNetworkSummaryQuery(true);

  if (isLoading) {
    return (
      <div className="ds-section-header">
        <h2>Rankings — Network Summary</h2>
        <span className="meta">Loading…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="ds-section-header">
        <h2>Rankings — Network Summary</h2>
        <span className="meta" style={{ color: '#dc2626' }}>
          Failed to load: {(error as Error).message}
        </span>
      </div>
    );
  }
  if (!data) return null;
  if (data.topPerformers.length === 0 && data.needsAttention.length === 0) return null;

  return (
    <div className="ds-reviews-row">
      <div className="ds-reviews-col">
        <div className="ds-section-header">
          <h2>Rankings — Top Performers</h2>
          <span className="meta">
            {data.topPerformers.length === 0
              ? 'No locations meet the top-performer bar yet'
              : `${data.topPerformers.length} locations · top 9 organic on all 3 keywords + ≥2 in top 3 + ≥2 in Maps 3-pack`}
          </span>
        </div>
        <div className="ds-panel ds-reviews-panel ds-reviews-panel--table">
          {data.topPerformers.length > 0 ? (
            <table className="ds-table ds-table-compact">
              <thead>
                <tr>
                  <th style={{ width: 28 }}></th>
                  <th>Franchisee</th>
                  <th className="num">Top 3</th>
                  <th className="num">3-Pack</th>
                  <th className="num">Best</th>
                </tr>
              </thead>
              <tbody>
                {data.topPerformers.map((p) => (
                  <TopRow key={p.afNumber} row={p} onDrillIn={onDrillIn} />
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: '40px 24px', textAlign: 'center', color: '#8a95a5', fontSize: 13 }}>
              No locations meet the bar yet.
            </div>
          )}
        </div>
      </div>

      <div className="ds-reviews-col">
        <div className="ds-section-header">
          <h2 style={{ color: '#dc2626' }}>Rankings — Needs Attention</h2>
          <span className="meta">
            {data.needsAttention.length === 0
              ? 'No 120+ day locations are missing all organic and Maps presence'
              : `${data.needsAttention.length} locations · open 120+ days with no top-10 organic and no 3-pack on any keyword`}
          </span>
        </div>
        <div className="ds-panel ds-reviews-panel ds-reviews-panel--table">
          {data.needsAttention.length > 0 ? (
            <table className="ds-table ds-table-compact">
              <thead>
                <tr>
                  <th style={{ width: 28 }}></th>
                  <th>Franchisee</th>
                  <th className="num">Days Open</th>
                  <th>Open Date</th>
                </tr>
              </thead>
              <tbody>
                {data.needsAttention.map((p) => (
                  <NeedsRow key={p.afNumber} row={p} onDrillIn={onDrillIn} />
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: '40px 24px', textAlign: 'center', color: '#8a95a5', fontSize: 13 }}>
              Everyone has at least some ranking signal.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TopRow({
  row,
  onDrillIn,
}: {
  row: RankingsTopPerformer;
  onDrillIn: (af: string) => void;
}) {
  return (
    <tr onClick={() => onDrillIn(row.afNumber)}>
      <td>
        <span className="ds-marker green" />
      </td>
      <td>
        <div className="ds-name">{row.name}</div>
        <div className="ds-sub">
          {row.afNumber}
          {row.location ? ` · ${row.location}` : ''}
        </div>
      </td>
      <td className="num">
        <span className="num-val">{row.topThreeCount}/3</span>
      </td>
      <td className="num">
        <span className="num-val">{row.packCount}/3</span>
      </td>
      <td className="num">
        <span className="num-val" style={{ color: '#15803d', fontWeight: 700 }}>
          #{row.bestRank}
        </span>
      </td>
    </tr>
  );
}

function NeedsRow({
  row,
  onDrillIn,
}: {
  row: RankingsLaggard;
  onDrillIn: (af: string) => void;
}) {
  return (
    <tr onClick={() => onDrillIn(row.afNumber)}>
      <td>
        <span className="ds-marker red" />
      </td>
      <td>
        <div className="ds-name">{row.name}</div>
        <div className="ds-sub">
          {row.afNumber}
          {row.location ? ` · ${row.location}` : ''}
        </div>
      </td>
      <td className="num">
        <span className="num-val">{row.daysOpen}</span>
      </td>
      <td style={{ fontSize: 12, color: '#5a6a7a' }}>{row.openDate}</td>
    </tr>
  );
}
